import { defineStore } from 'pinia';
import CaptainPreferencesAPI from 'dashboard/api/captain/preferences';

export const useCaptainConfigStore = defineStore('captainConfig', {
  state: () => ({
    providers: {},
    models: {},
    features: {},
    aiSettings: {},
    usage: {},
    keyValidationResult: null,
    uiFlags: {
      isFetching: false,
      isFetchingAiSettings: false,
      isFetchingUsage: false,
      isSavingAiSettings: false,
      isValidatingKey: false,
    },
  }),

  getters: {
    getProviders: state => state.providers,
    getModels: state => state.models,
    getFeatures: state => state.features,
    getAiSettings: state => state.aiSettings,
    getUsage: state => state.usage,
    getKeyValidationResult: state => state.keyValidationResult,
    getUIFlags: state => state.uiFlags,
    getModelsForFeature: state => featureKey => {
      const feature = state.features[featureKey];
      const models = feature?.models || [];

      const providerOrder = { openai: 0, anthropic: 1, gemini: 2 };

      return [...models].sort((a, b) => {
        if (a.coming_soon && !b.coming_soon) return 1;
        if (!a.coming_soon && b.coming_soon) return -1;

        const providerA = providerOrder[a.provider] ?? 999;
        const providerB = providerOrder[b.provider] ?? 999;
        if (providerA !== providerB) return providerA - providerB;

        return (b.credit_multiplier || 0) - (a.credit_multiplier || 0);
      });
    },
    getDefaultModelForFeature: state => featureKey => {
      const feature = state.features[featureKey];
      return feature?.default || null;
    },
    getSelectedModelForFeature: state => featureKey => {
      const feature = state.features[featureKey];
      return feature?.selected || feature?.default || null;
    },
    getModelsForProvider: state => provider => {
      const allModels = state.models;
      return Object.entries(allModels)
        .filter(([, m]) => m.provider === provider)
        .map(([id, m]) => ({ id, ...m }));
    },
    copilotConfig: state => state.aiSettings.config?.copilot || {},
    insightsConfig: state => state.aiSettings.config?.insights || {},
    alertsConfig: state => state.aiSettings.config?.alerts || {},
  },

  actions: {
    async fetch() {
      this.uiFlags.isFetching = true;
      try {
        const response = await CaptainPreferencesAPI.get();
        this.providers = response.data.providers || {};
        this.models = response.data.models || {};
        this.features = response.data.features || {};
      } catch (error) {
        // Ignore error
      } finally {
        this.uiFlags.isFetching = false;
      }
    },

    async updatePreferences(data) {
      const response = await CaptainPreferencesAPI.updatePreferences(data);
      this.providers = response.data.providers || {};
      this.models = response.data.models || {};
      this.features = response.data.features || {};
    },

    async fetchAiSettings() {
      this.uiFlags.isFetchingAiSettings = true;
      try {
        const response = await CaptainPreferencesAPI.getAiSettings();
        this.aiSettings = response.data || {};
      } catch (error) {
        // Ignore error
      } finally {
        this.uiFlags.isFetchingAiSettings = false;
      }
    },

    async updateAiSettings(data) {
      this.uiFlags.isSavingAiSettings = true;
      try {
        const response = await CaptainPreferencesAPI.updateAiSettings(data);
        this.aiSettings = { ...this.aiSettings, ...response.data };
        return response.data;
      } finally {
        this.uiFlags.isSavingAiSettings = false;
      }
    },

    async fetchUsage() {
      this.uiFlags.isFetchingUsage = true;
      try {
        const response = await CaptainPreferencesAPI.getUsage();
        this.usage = response.data || {};
      } catch (error) {
        // Ignore error
      } finally {
        this.uiFlags.isFetchingUsage = false;
      }
    },

    async validateKey({ api_key, provider, api_endpoint }) {
      this.uiFlags.isValidatingKey = true;
      this.keyValidationResult = null;
      try {
        const response = await CaptainPreferencesAPI.validateKey({
          api_key,
          provider,
          api_endpoint,
        });
        this.keyValidationResult = response.data;
        return response.data;
      } catch (error) {
        const result = error.response?.data || {
          valid: false,
          message: 'Connection failed',
        };
        this.keyValidationResult = result;
        return result;
      } finally {
        this.uiFlags.isValidatingKey = false;
      }
    },

    clearKeyValidation() {
      this.keyValidationResult = null;
    },
  },
});
